import { useEffect, useState } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { z } from "zod";
import { getSubjectUEById, updateSubjectUE } from "../../../api/ue";
import { getAllSubject } from "../../../api/pedagogicElement";
import { getAllTeachers } from "../../../api/teachers";
import { getCoursesByAcademicYear } from "../../../api/cours";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
//Typage des props parents
type UUID = string;
type Props = {
  show: boolean;
  ueSubjectID: UUID;
  classID: UUID;
  onClose: () => void;
  onSaved?: () => void;
};

interface Teacher {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  sexe: String;
  number: String;
  adresse: String;
  birthday: String;
  statut: boolean;
  createdAt: string;
}

const EditSubjectUEModal = ({ show, onClose, onSaved, ueSubjectID, classID }: Props) => {
  //Initialisation de l'année academmique actuelle
  const { selectedYear } = useAcademicYear();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [linkedCourse, setLinkedCourse] = useState(false);
  const [mode, setMode] = useState<"course" | "subject">("course");
  const [subjects, setSubjects] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);

  //Schéma de notre formulaire
  const createSchema = z.object({
    credit: z
      .number({ invalid_type_error: "Le credit de l'ECU est requis" })
      .positive("Le crédit doit être supérieur à zéro"),
    codeECU: z.string().nonempty("Le code de l'UCE est requis"),
    coefficient: z
      .number({ invalid_type_error: "Le coefficient de l'UCE est requis" })
      .min(0, "Le coefficient doit être au minimum 0"),
    id_teacher: z.string().nonempty("Le professeur est requis"),
    id_ue: z.string().nonempty("L'UE est requise"),
    id_course: z.string().optional(),
    id_subject: z.string().optional(),
  }).superRefine((data, ctx) => {
    const field = mode === "course" ? "id_course" : "id_subject";
    if (!data[field]) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: mode === "course" ? "Le cours est requis" : "La matière est requise" });
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
    defaultValues: { id_course: "", id_subject: "", id_teacher: "" },
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour création vers le backend
      await updateSubjectUE({ ...data, id_course: mode === "course" ? data.id_course : null, id_subject: mode === "subject" ? data.id_subject : undefined }, ueSubjectID);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("ECU mise à jour !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      /// Si erreur (Toast Error)
      // console.log(error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  //Fonction de récuperation des ECUs
  const fetchSubjectUE = async () => {
    try {
      const response = await getSubjectUEById(
        ueSubjectID,
        buildPermissionContext(Resource.SUBJECT_UE, Action.UPDATE),
      );

      if (!response) {
        // Si aucune année trouvé
        throw new Error("ECU introuvable");
      }

      setLinkedCourse(Boolean(response.data.id_course));
      setMode(response.data.id_course ? "course" : "subject");
      reset({
        // Si année trouvé on rempli les champs du formulaire
        credit: response.data.credit,
        codeECU: response.data.codeECU,
        coefficient: response.data.coefficient ?? 0,
        id_course: response.data.id_course || "",
        id_subject: response.data.id_subject,
        id_teacher: response.data.id_teacher,
        id_ue: response.data.id_ue,
      });
      const coursesResponse = await getCoursesByAcademicYear(selectedYear?.id);
      setCourses((coursesResponse.data || []).filter((course: any) =>
        course.id === response.data.id_course || (course.status === "Approuvé" &&
        course.id_classes?.includes(classID) &&
        !course.class_assignments?.find((assignment: any) => assignment.id_class === classID)?.id_ue)
      ));
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  //Fonction de récuperation des profs
  const fetchTeachers = async () => {
    try {
      const response = await getAllTeachers(
        buildPermissionContext(Resource.SUBJECT_UE, Action.UPDATE),
      );
      const teachers = response.data;
      setTeachers(teachers);
    } catch (error: any) {
      // console.log("erreur de chargement des Profs : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
      // Option: reset les profs en cas d'erreur
      setTeachers([]);
    }
  };

  useEffect(() => {
    if (!show || !ueSubjectID) return; // ✅ Ne rien faire si le modal n'est pas ouvert

    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;

    fetchTeachers(); // On fetch les profs

    fetchSubjectUE();
  }, [show, ueSubjectID, reset, classID, selectedYear?.id]);

  useEffect(() => {
    const course = courses.find((item) => item.id === watch("id_course"));
    if (course?.TeacherProfil?.id) setValue("id_teacher", course.TeacherProfil.id);
  }, [watch("id_course"), courses]);

  useEffect(() => {
    if (!show || !selectedYear?.id) return;
    let active = true;
    getAllSubject(selectedYear.id, buildPermissionContext(Resource.SUBJECT_UE, Action.UPDATE)).then(response => {
      if (active) setSubjects(response.data || []);
    }).catch(() => { if (active) toast.error("Impossible de charger les matières"); });
    return () => { active = false; };
  }, [show, selectedYear?.id]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier un ECU</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <input type="hidden" {...register("id_ue")} />
            <div className="row">
              <div className="col-12 mb-3">
                <label className="form-label">Rattachement de l’ECU</label>
                <select className="form-select" disabled={linkedCourse} value={mode} onChange={e => { setMode(e.target.value as "course" | "subject"); setValue("id_course", ""); }}>
                  <option value="course">Un cours</option>
                  <option value="subject">Une matière sans cours</option>
                </select>
              </div>
              {mode === "subject" && <div className="col-12 mb-3">
                <label className="form-label">Matière</label>
                <select {...register("id_subject")} className={errors.id_subject ? "form-select is-invalid" : "form-select"}>
                  <option value="">Sélectionner une matière</option>
                  {subjects.map(subject => <option key={subject.id} value={subject.id}>{subject.label}</option>)}
                </select>
                {errors.id_subject && <div className="invalid-feedback">{errors.id_subject.message}</div>}
              </div>}
              {mode === "course" && <>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Cours réel</label>
                  <select
                    {...register("id_course")}
                    aria-label="Default select example"
                    className={
                      errors.id_course
                        ? "form-select is-invalid"
                        : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.Subject?.label} — {course.TeacherProfil?.firstname} {course.TeacherProfil?.lastname}
                      </option>
                    ))}
                  </select>
                  {errors.id_course && (
                    <div className="invalid-feedback">
                      {errors.id_course.message}
                    </div>
                  )}
                </div>
              </div>

              </>}

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Professeur</label>
                  <select
                    {...register("id_teacher")}
                    disabled={mode === "course"}
                    aria-label="Default select example"
                    className={
                      errors.id_teacher
                        ? "form-select is-invalid"
                        : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.firstname} {teacher.lastname}
                      </option>
                    ))}
                  </select>
                  {errors.id_teacher && (
                    <div className="invalid-feedback">
                      {errors.id_teacher.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Code ECU
                  </label>
                  <input
                    {...register("codeECU")}
                    type="text"
                    className={
                      errors.codeECU
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Code ECU"
                  />
                  {errors.codeECU && (
                    <div className="invalid-feedback">
                      {errors.codeECU.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Crédit
                  </label>
                  <input
                    {...register("credit", { valueAsNumber: true })}
                    type="number"
                    className={
                      errors.credit ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: UE-MGT"
                  />
                  {errors.credit && (
                    <div className="invalid-feedback">
                      {errors.credit.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Coefficient
                  </label>
                  <input
                    {...register("coefficient", { valueAsNumber: true })}
                    type="number"
                    className={
                      errors.coefficient
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: UE-MGT"
                  />
                  {errors.coefficient && (
                    <div className="invalid-feedback">
                      {errors.coefficient.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              // disabled={isSubmitting||!selectedYear?.statut}
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditSubjectUEModal,
  [buildPermission(Resource.SUBJECT_UE, Action.UPDATE)],
  "masquer",
);
